/**
 * Vervit — OTP WhatsApp login (theme frontend)
 * Requires window.VRV_OTP_AUTH { apiBase, authToken, returnTo }
 */
(function () {
  const root = document.querySelector('[data-vrv-otp-auth]');
  if (!root) return;

  const cfg = window.VRV_OTP_AUTH || {};
  const apiBase = String(cfg.apiBase || '').replace(/\/$/, '');
  const authToken = cfg.authToken || '';
  const returnTo = cfg.returnTo || '/account';

  const phoneStep = root.querySelector('[data-vrv-otp-step="phone"]');
  const verifyStep = root.querySelector('[data-vrv-otp-step="verify"]');
  const phoneInput = root.querySelector('[data-vrv-otp-phone]');
  const digitInputs = Array.from(root.querySelectorAll('[data-vrv-otp-digit]'));
  const sendBtn = root.querySelector('[data-vrv-otp-send]');
  const verifyBtn = root.querySelector('[data-vrv-otp-verify]');
  const resendBtn = root.querySelector('[data-vrv-otp-resend]');
  const backBtn = root.querySelector('[data-vrv-otp-back]');
  const alertEl = root.querySelector('[data-vrv-otp-alert]');
  const maskedEl = root.querySelector('[data-vrv-otp-masked]');
  const titleEl = root.querySelector('[data-vrv-otp-title]');
  const subtitleEl = root.querySelector('[data-vrv-otp-subtitle]');
  const stepDots = {
    phone: root.querySelector('[data-vrv-otp-step-dot="phone"]'),
    verify: root.querySelector('[data-vrv-otp-step-dot="verify"]'),
  };

  const COPY = {
    phone: {
      title: 'Login with mobile',
      subtitle: 'Enter your number — we’ll send a one-time code on WhatsApp. No password needed.',
    },
    verify: {
      title: 'Verify your number',
      subtitle: 'Check WhatsApp for your 4-digit code. It may take a few seconds to arrive.',
    },
  };

  let currentPhone = '';
  let resendTimer = null;
  // FIX (root cause of "logged in successfully then immediately logged
  // out"): verifyOtp() could previously be triggered twice in close
  // succession -- once from handleDigitInput() auto-submitting the moment
  // the 4th digit lands (common with mobile SMS/WhatsApp OTP autofill,
  // which often also dispatches an Enter/Go keypress as part of the same
  // autofill gesture) and again from handleDigitKeydown()'s Enter handler.
  // setLoading(verifyBtn, true) only disables the button -- it doesn't stop
  // the auto-submit path, which isn't gated by the button at all. Two
  // concurrent /otp/verify calls for the same OTP could each get back a
  // login/redirect response and each fire their own
  // window.location.href / form.submit(), with the second one interrupting
  // the first mid-navigation (and, for the multipass path, racing against a
  // token that's meant to be single-use) -- landing the browser on a page
  // where the session never actually took, which looks exactly like an
  // instant logout right after a successful login.
  let otpVerifyInFlight = false;

  function resetPhoneField() {
    currentPhone = '';
    if (phoneInput) phoneInput.value = '';
    if (maskedEl) maskedEl.textContent = '';
  }

  function beatPhoneAutofill() {
    if (currentPhone) return;
    resetPhoneField();
    window.setTimeout(function () {
      if (!currentPhone) resetPhoneField();
    }, 100);
    window.setTimeout(function () {
      if (!currentPhone) resetPhoneField();
    }, 400);
  }

  const SPIN_LEAD_KEY = 'vrv_spin_lead_v1';
  const SPIN_PHONE_KEY = 'vrv_spin_phone_v1';
  const SPIN_ATTR_KEYS = [
    'Phone (Spin Wheel)',
    'Phone (10-digit)',
    'Spin Reward',
    'Spin Reward Code',
    'Spin Played At',
    'spin_wheel_phone',
    'spin_wheel_prize',
    'spin_wheel_code',
    'spin_wheel_at',
  ];

  function clearSpinWheelGuestLead() {
    try {
      localStorage.removeItem(SPIN_LEAD_KEY);
      localStorage.removeItem(SPIN_PHONE_KEY);
    } catch (_) {}
  }

  async function clearSpinCartAttributes() {
    try {
      const res = await fetch('/cart.js', {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      });
      if (!res.ok) return;
      const cart = await res.json();
      const attrs = cart.attributes || {};
      const cleared = {};
      let dirty = false;
      SPIN_ATTR_KEYS.forEach(function (key) {
        if (attrs[key]) {
          cleared[key] = '';
          dirty = true;
        }
      });
      if (!dirty) return;
      await fetch('/cart/update.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-VSW-Internal': '1',
        },
        body: JSON.stringify({ attributes: cleared }),
      });
    } catch (_) {}
  }

  function humanizeError(message) {
    const msg = String(message || '').trim();
    if (!msg) return 'Something went wrong. Please try again.';

    if (/GraphQL errors/i.test(msg) || /INVALID_VARIABLE/i.test(msg)) {
      return 'We could not sign you in right now. Please try again in a moment.';
    }
    if (/Fast2SMS|WhatsApp/i.test(msg) && /fail|error|could not/i.test(msg)) {
      return 'Could not send the WhatsApp code. Please check your number and try again.';
    }
    if (/invalid otp|wrong otp|incorrect/i.test(msg)) {
      return 'That code is incorrect. Please check WhatsApp and try again.';
    }
    if (/expired/i.test(msg)) {
      return 'This code has expired. Tap Resend OTP to get a new one.';
    }
    if (msg.length > 140) return msg.slice(0, 140) + '…';
    return msg;
  }

  function showAlert(message, type) {
    if (!alertEl) return;
    if (!message) {
      alertEl.hidden = true;
      alertEl.innerHTML = '';
      alertEl.classList.remove('vrv-auth__alert--success');
      return;
    }

    const icon =
      type === 'success'
        ? '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="20 6 9 17 4 12"/></svg>'
        : '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>';

    alertEl.hidden = false;
    alertEl.classList.toggle('vrv-auth__alert--success', type === 'success');
    alertEl.innerHTML = icon + '<span>' + humanizeError(message) + '</span>';
  }

  function setLoading(btn, loading) {
    if (!btn) return;
    btn.disabled = loading;
    btn.classList.toggle('is-loading', loading);
  }

  function apiHeaders() {
    const headers = { 'Content-Type': 'application/json' };
    if (authToken) headers['X-Auth-Token'] = authToken;
    return headers;
  }

  async function apiPost(path, body) {
    if (!apiBase) throw new Error('Login service is not configured.');
    const resp = await fetch(apiBase + path, {
      method: 'POST',
      headers: apiHeaders(),
      body: JSON.stringify(body),
    });
    const data = await resp.json().catch(function () {
      return {};
    });
    if (!resp.ok || !data.ok) {
      const err = new Error(data.error || data.message || 'Request failed.');
      err.retryAfter = data.retry_after;
      throw err;
    }
    return data;
  }

  function getOtpCode() {
    return digitInputs.map(function (el) {
      return String(el.value || '').replace(/\D/g, '').slice(0, 1);
    }).join('');
  }

  function clearPin() {
    digitInputs.forEach(function (el) {
      el.value = '';
      el.classList.remove('is-filled');
    });
  }

  function focusPin(index) {
    const el = digitInputs[index];
    if (el) el.focus();
  }

  function updateStepChrome(step) {
    const copy = COPY[step] || COPY.phone;
    if (titleEl) titleEl.textContent = copy.title;
    if (subtitleEl) subtitleEl.textContent = copy.subtitle;

    Object.keys(stepDots).forEach(function (key) {
      stepDots[key]?.classList.toggle('is-active', key === step);
      stepDots[key]?.classList.toggle('is-done', step === 'verify' && key === 'phone');
    });
  }

  function showStep(step) {
    if (phoneStep) phoneStep.hidden = step !== 'phone';
    if (verifyStep) verifyStep.hidden = step !== 'verify';
    updateStepChrome(step);
    showAlert('');
  }

  function normalizePhoneInput() {
    let digits = String(phoneInput?.value || '').replace(/\D/g, '');
    if (digits.length === 12 && digits.startsWith('91')) digits = digits.slice(2);
    if (digits.length === 11 && digits.startsWith('0')) digits = digits.slice(1);
    return digits;
  }

  function startResendCooldown(seconds) {
    if (!resendBtn) return;
    let left = seconds;
    resendBtn.disabled = true;
    resendBtn.textContent = 'Resend in ' + left + 's';
    clearInterval(resendTimer);
    resendTimer = setInterval(function () {
      left -= 1;
      if (left <= 0) {
        clearInterval(resendTimer);
        resendBtn.disabled = false;
        resendBtn.textContent = 'Resend OTP';
        return;
      }
      resendBtn.textContent = 'Resend in ' + left + 's';
    }, 1000);
  }

  // The resend countdown belongs to the number it was started for. A different
  // number must never inherit it (or its "please wait" message).
  function resetResendCooldown() {
    clearInterval(resendTimer);
    resendTimer = null;
    if (resendBtn) {
      resendBtn.disabled = false;
      resendBtn.textContent = 'Resend OTP';
    }
  }

  async function sendOtp() {
    const phone = normalizePhoneInput();
    if (phone.length !== 10 || !/^[6-9]/.test(phone)) {
      showAlert('Enter a valid 10-digit mobile number.');
      phoneInput?.focus();
      return;
    }

    if (phone !== currentPhone) {
      resetResendCooldown();
    }

    setLoading(sendBtn, true);
    showAlert('');

    try {
      const data = await apiPost('/otp/send', { phone: phone });
      currentPhone = phone;
      if (maskedEl) maskedEl.textContent = data.phone_masked || ('+91 ' + phone);
      clearPin();
      showStep('verify');
      focusPin(0);
      startResendCooldown(Number(data.expires_in) > 60 ? 60 : 30);
      showAlert(data.message || 'OTP sent on WhatsApp.', 'success');
    } catch (err) {
      showAlert(err.message || 'Could not send OTP.');
      if (err.retryAfter) startResendCooldown(Number(err.retryAfter));
    } finally {
      setLoading(sendBtn, false);
    }
  }

  async function syncCustomerIdentityAfterLogin(login) {
    const email = String(login && login.email ? login.email : '').trim();
    const phoneRaw = String(currentPhone || '').trim();
    const phoneE164 =
      phoneRaw.length === 10 ? '+91' + phoneRaw : phoneRaw.replace(/\s/g, '');
    if (!email && !phoneE164) return;

    if (email) {
      document.body.setAttribute('data-vrv-customer-email', email);
    }
    if (phoneE164) {
      document.body.setAttribute('data-vrv-customer-phone', phoneE164);
    }
    document.body.setAttribute('data-vrv-customer-logged-in', '1');
    document.body.classList.add('customer-logged-in');

    if (typeof window.vrvSaveCheckoutIdentity === 'function') {
      window.vrvSaveCheckoutIdentity(email, phoneE164);
    }

    const cfgEl = document.getElementById('RazorpayMagicCheckoutConfig');
    if (cfgEl) {
      try {
        const cfg = JSON.parse(cfgEl.textContent || '{}');
        if (email) cfg.email = email;
        if (phoneE164) cfg.phone = phoneE164;
        cfgEl.textContent = JSON.stringify(cfg);
      } catch (_) {}
    }

    const cartAttrs = {};
    if (email) {
      cartAttrs.checkout_email = email;
      cartAttrs.contact_email = email;
    }
    if (phoneE164) {
      cartAttrs.checkout_phone = phoneE164;
      cartAttrs.contact_phone = phoneE164;
    }

    if (Object.keys(cartAttrs).length) {
      try {
        await fetch('/cart/update.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-VSW-Internal': '1',
          },
          body: JSON.stringify({ attributes: cartAttrs }),
        });
      } catch (_) {}
    }

    await postLoginDraftSync(phoneE164);
    if (window.vrvUserSync && typeof window.vrvUserSync.sync === 'function') {
      await window.vrvUserSync.sync({
        isLoginMerge: true,
        phone: phoneE164,
        email: email,
      }).catch(function () {});
    }
  }

  async function postLoginDraftSync(phoneE164) {
    const cfg = window.vrvUserSyncConfig || {};
    const url = String(cfg.spinWebhookUrl || '').trim();
    if (!url || !phoneE164) return;

    let cart = null;
    try {
      const res = await fetch('/cart.js', {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      });
      if (res.ok) cart = await res.json();
    } catch (_) {}

    const payload = {
      event: 'otp_login',
      shop: window.shopUrl ? String(window.shopUrl).replace(/^https?:\/\//, '') : '',
      phone: phoneE164,
      phone10: phoneE164.replace(/^\+91/, ''),
      source: 'otp_logged_in',
      url: window.location.href,
      at: new Date().toISOString(),
    };

    if (cart) {
      payload.cartToken = cart.token;
      payload.cart = {
        token: cart.token,
        item_count: cart.item_count,
        total_price: cart.total_price,
        currency: cart.currency,
        items: (cart.items || []).map(function (it) {
          return {
            variant_id: it.variant_id,
            product_id: it.product_id,
            title: it.product_title || it.title,
            quantity: it.quantity,
            price: it.price,
            source: 'cart',
          };
        }),
      };
    }

    const body = JSON.stringify(payload);
    try {
      await fetch(url, {
        method: 'POST',
        credentials: 'omit',
        mode: 'cors',
        keepalive: true,
        headers: { 'Content-Type': 'text/plain;charset=UTF-8' },
        body: body,
      });
    } catch (_) {
      try {
        const blob = new Blob([body], { type: 'text/plain;charset=UTF-8' });
        if (navigator.sendBeacon) navigator.sendBeacon(url, blob);
      } catch (err) {
        /* ignore */
      }
    }
  }

  function submitShopifyLoginForm(login) {
    const form = document.createElement('form');
    form.method = 'POST';
    form.action = login.action || '/account/login';
    form.style.display = 'none';

    const fields = {
      'customer[email]': login.email,
      'customer[password]': login.password,
      form_type: 'customer_login',
      utf8: '✓',
    };

    if (login.return_to) {
      fields.return_url = login.return_to;
    }

    Object.keys(fields).forEach(function (name) {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      input.value = fields[name];
      form.appendChild(input);
    });

    document.body.appendChild(form);
    form.submit();
  }

  async function submitShopifyLogin(login) {
    await syncCustomerIdentityAfterLogin(login);

    // FIX (root cause of "laptop has 8 products, mobile shows 0/different" for
    // the SAME account): this store has no Multipass secret configured, so
    // every real login goes through THIS password-login path -- the
    // multipass branch above (which already does this) never runs in
    // production. Without an explicit login-merge sync here, cart merging
    // only happened via boot()'s implicit "identity changed since last visit"
    // check, which is false (and so skips the merge) on any device that has
    // logged in with this phone before -- exactly the case where a customer
    // is re-logging in on their laptop or phone and expects their existing
    // account cart to show up. Merging explicitly on every login, like the
    // multipass path already does, makes that reliable regardless of whether
    try {
      const phoneE164 =
        currentPhone && currentPhone.length === 10 ? '+91' + currentPhone : currentPhone || '';
      if (phoneE164) localStorage.setItem('vrvCheckoutPhone', phoneE164);
      if (login && login.email) localStorage.setItem('vrvCheckoutEmail', login.email);
    } catch (_) {}

    if (window.vrvUserSync && typeof window.vrvUserSync.sync === 'function') {
      const phoneE164 =
        currentPhone && currentPhone.length === 10 ? '+91' + currentPhone : currentPhone || '';
      await window.vrvUserSync
        .sync({ isLoginMerge: true, phone: phoneE164, email: login && login.email })
        .catch(function () {});
    }

    submitShopifyLoginForm(login);
  }

  async function verifyOtp() {
    if (otpVerifyInFlight) return;

    const otp = getOtpCode();
    if (otp.length !== 4) {
      showAlert('Enter the 4-digit OTP from WhatsApp.');
      focusPin(otp.length);
      return;
    }

    otpVerifyInFlight = true;
    setLoading(verifyBtn, true);
    showAlert('');

    try {
      const data = await apiPost('/otp/verify', {
        phone: currentPhone,
        otp: otp,
        return_to: returnTo,
      });

      if (data.login_method === 'multipass' && data.redirect_url) {
        const phoneE164 = currentPhone.length === 10 ? '+91' + currentPhone : currentPhone;
        document.body.setAttribute('data-vrv-customer-phone', phoneE164);
        document.body.setAttribute('data-vrv-customer-logged-in', '1');
        document.body.classList.add('customer-logged-in');
        if (typeof window.vrvSaveCheckoutIdentity === 'function') {
          window.vrvSaveCheckoutIdentity('', phoneE164);
        }
        await postLoginDraftSync(phoneE164);
        if (window.vrvUserSync && typeof window.vrvUserSync.sync === 'function') {
          await window.vrvUserSync.sync({
            isLoginMerge: true,
            phone: phoneE164,
          }).catch(function () {});
        }
        window.location.href = data.redirect_url;
        return;
      }

      if (data.login) {
        clearSpinWheelGuestLead();
        await clearSpinCartAttributes();
        showAlert('Signing you in…', 'success');
        await submitShopifyLogin(data.login);
        return;
      }

      showAlert('Login succeeded but session could not be started. Please try again.');
    } catch (err) {
      showAlert(err.message || 'Verification failed.');
      clearPin();
      focusPin(0);
    } finally {
      otpVerifyInFlight = false;
      setLoading(verifyBtn, false);
    }
  }

  function handleDigitInput(index, el) {
    const digit = String(el.value || '').replace(/\D/g, '').slice(-1);
    el.value = digit;
    el.classList.toggle('is-filled', !!digit);

    if (digit && index < digitInputs.length - 1) {
      focusPin(index + 1);
    }

    if (getOtpCode().length === 4) {
      verifyOtp();
    }
  }

  function handleDigitKeydown(index, e) {
    if (e.key === 'Backspace' && !digitInputs[index].value && index > 0) {
      e.preventDefault();
      focusPin(index - 1);
      digitInputs[index - 1].value = '';
      digitInputs[index - 1].classList.remove('is-filled');
      return;
    }

    if (e.key === 'ArrowLeft' && index > 0) {
      e.preventDefault();
      focusPin(index - 1);
    }

    if (e.key === 'ArrowRight' && index < digitInputs.length - 1) {
      e.preventDefault();
      focusPin(index + 1);
    }

    if (e.key === 'Enter') {
      e.preventDefault();
      verifyOtp();
    }
  }

  function handleDigitPaste(e) {
    e.preventDefault();
    const pasted = (e.clipboardData?.getData('text') || '').replace(/\D/g, '').slice(0, 4);
    if (!pasted) return;

    pasted.split('').forEach(function (ch, i) {
      if (!digitInputs[i]) return;
      digitInputs[i].value = ch;
      digitInputs[i].classList.toggle('is-filled', true);
    });

    focusPin(Math.min(pasted.length, digitInputs.length - 1));
    if (pasted.length === 4) verifyOtp();
  }

  sendBtn?.addEventListener('click', function (e) {
    e.preventDefault();
    sendOtp();
  });

  verifyBtn?.addEventListener('click', function (e) {
    e.preventDefault();
    verifyOtp();
  });

  resendBtn?.addEventListener('click', function (e) {
    e.preventDefault();
    if (phoneInput) phoneInput.value = currentPhone;
    clearPin();
    sendOtp();
  });

  backBtn?.addEventListener('click', function (e) {
    e.preventDefault();
    clearPin();
    resetResendCooldown();
    showAlert('');
    resetPhoneField();
    showStep('phone');
    phoneInput?.focus();
  });

  phoneInput?.addEventListener('keydown', function (e) {
    if (e.key === 'Enter') {
      e.preventDefault();
      sendOtp();
    }
  });

  digitInputs.forEach(function (el, index) {
    el.addEventListener('input', function () {
      handleDigitInput(index, el);
    });
    el.addEventListener('keydown', function (e) {
      handleDigitKeydown(index, e);
    });
    el.addEventListener('paste', handleDigitPaste);
    el.addEventListener('focus', function () {
      el.select();
    });
  });

  resetPhoneField();
  showStep('phone');
  beatPhoneAutofill();

  window.addEventListener('pageshow', function (e) {
    if (e.persisted && !currentPhone) {
      resetPhoneField();
      clearPin();
      showStep('phone');
    }
  });
})();
